import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SvgIcon } from '../../components/icons';
import { DsRoot } from '../ThemeProvider';
import { Mode, THEMES } from '../themes';
import { ThemePoster } from './ThemePoster';

/**
 * All five directions on one screen, in the same mode, with identical content.
 *
 * The mode toggle applies to every poster at once — comparing Aurora Noir's
 * dark against Precision's light tells you nothing useful, so the comparison
 * has to be like-for-like.
 */
export const CompareView = () => {
  const [mode, setMode] = useState<Mode | 'native'>('native');

  return (
    <DsRoot theme="precision" mode="light">
      <div style={{ background: 'var(--ds-bg)', minHeight: '100vh' }}>
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px 20px 56px' }}>
          <div className="ds-page-header">
            <div>
              <Link
                to="/design"
                className="ds-muted flex items-center gap-1"
                style={{ fontSize: 'var(--ds-fs-xs)' }}
              >
                <SvgIcon name="keyboardArrowLeft" size={13} />
                All directions
              </Link>
              <h1 className="ds-title" style={{ fontSize: 26, marginTop: 4 }}>
                Side by side
              </h1>
              <p className="ds-subtitle">
                Same screen, same data, five token sets.
              </p>
            </div>

            <div className="ds-segmented" role="group" aria-label="Preview mode">
              {(
                [
                  ['native', 'Each theme’s own'],
                  ['light', 'All light'],
                  ['dark', 'All dark'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="ds-segment"
                  aria-pressed={mode === value}
                  onClick={() => setMode(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-5">
            {THEMES.map((t) => (
              <div
                key={t.id}
                className="min-w-0"
                style={{ flex: '1 1 480px', maxWidth: 760 }}
              >
                <div
                  className="flex items-center justify-between"
                  style={{ marginBottom: 8 }}
                >
                  <div className="min-w-0">
                    <div className="ds-section-title">{t.name}</div>
                    <div
                      className="ds-muted"
                      style={{ fontSize: 'var(--ds-fs-xs)' }}
                    >
                      {t.tagline}
                    </div>
                  </div>
                  <Link
                    to={`/design/${t.id}`}
                    className="ds-btn"
                    data-ds-variant="ghost"
                    data-ds-size="sm"
                  >
                    Open
                    <SvgIcon name="keyboardArrowRight" size={13} />
                  </Link>
                </div>
                <ThemePoster
                  theme={t}
                  mode={mode === 'native' ? t.defaultMode : mode}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </DsRoot>
  );
};
